import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Checkbox,
  Chip,
  Avatar,
  IconButton,
  Toolbar,
  Tooltip,
  MenuItem,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputBase,
  alpha,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link as MuiLink,
  FormControl,
  Select,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Language as LanguageIcon,
  Computer as ComputerIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  FiberManualRecord as FiberManualRecordIcon,
  NavigateNext as NavigateNextIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Notes as NotesIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import {
  GET_LEADS,
  GET_ALL_WIDGETS,
  UPDATE_VISITOR_INFO,
  UPDATE_LEAD_STATUS,
  ADD_LEAD_NOTE,
} from "./graphql/queries";

// Styled components
const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(1),
    width: "auto",
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      width: "20ch",
      "&:focus": {
        width: "30ch",
      },
    },
  },
}));

// Helper functions
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

// Get status color
const getStatusColor = (status) => {
  switch (status) {
    case "New":
      return "primary";
    case "Contacted":
      return "warning";
    case "Qualified":
      return "success";
    case "Converted":
      return "secondary";
    case "Lost":
      return "error";
    default:
      return "default";
  }
};

// Table head cells
const headCells = [
  { id: "firstSeen", label: "Date Captured", align: "left" },
  { id: "name", label: "Name", align: "left" },
  { id: "email", label: "Email", align: "left" },
  { id: "phone", label: "Phone", align: "left" },
  { id: "company", label: "Company", align: "left" },
  { id: "leadStatus", label: "Status", align: "center" },
  { id: "widgetId", label: "Source", align: "left" },
  { id: "lastActiveAt", label: "Last Activity", align: "left" },
];

// Table head component
function EnhancedTableHead(props) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.align}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            {headCell.id !== "leadStatus" ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : "asc"}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
              </TableSortLabel>
            ) : (
              headCell.label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

// Lead Card Component for Card View
const LeadCard = ({ lead, isSelected, onSelect, onViewDetails }) => {
  return (
    <Card>
      <div style={{ position: "relative" }}>
        <div
          style={{
            backgroundColor: "#f0f0f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "140px",
          }}
        >
          <Typography variant="h2" color="textSecondary">
            {lead.name ? lead.name.charAt(0).toUpperCase() : "L"}
          </Typography>
        </div>
        <Checkbox
          checked={isSelected}
          onChange={(e) => onSelect(e, lead.id)}
          style={{ position: "absolute", top: "8px", left: "8px" }}
        />
        <Chip
          label={lead.leadStatus || "New"}
          color={getStatusColor(lead.leadStatus || "New")}
          size="small"
          style={{ position: "absolute", top: "8px", right: "8px" }}
        />
      </div>
      <CardContent>
        <Box>
          <Typography variant="h6" gutterBottom noWrap>
            {lead.name}
          </Typography>
        </Box>
        <Box display="flex" flexDirection="column" gap={1} mt={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <EmailIcon fontSize="small" color="action" />
            <Typography variant="body2" color="textSecondary" noWrap>
              {lead.email}
            </Typography>
          </Box>
          {lead.phone && (
            <Box display="flex" alignItems="center" gap={1}>
              <PhoneIcon fontSize="small" color="action" />
              <Typography variant="body2" color="textSecondary">
                {lead.phone}
              </Typography>
            </Box>
          )}
          {lead.company && (
            <Box display="flex" alignItems="center" gap={1}>
              <BusinessIcon fontSize="small" color="action" />
              <Typography variant="body2" color="textSecondary">
                {lead.company}
              </Typography>
            </Box>
          )}
          <Box display="flex" alignItems="center" gap={1}>
            <LanguageIcon fontSize="small" color="action" />
            <Typography variant="body2" color="textSecondary">
              {lead.city}, {lead.country}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <ScheduleIcon fontSize="small" color="action" />
            <Typography variant="body2" color="textSecondary">
              {lead.lastActiveAt
                ? new Date(
                    typeof lead.lastActiveAt === "string"
                      ? Number(lead.lastActiveAt)
                      : lead.lastActiveAt
                  ).toLocaleString()
                : "N/A"}
            </Typography>
          </Box>
        </Box>
        <Box mt={2}>
          <Button
            size="small"
            variant="outlined"
            fullWidth
            startIcon={<VisibilityIcon />}
            onClick={() => onViewDetails(lead)}
          >
            View Details
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

// Lead Details Modal
function LeadDetailsModal({ lead, open, onClose, refetchLeads }) {
  const [activeTab, setActiveTab] = useState(0);
  const [editingField, setEditingField] = useState(null);
  const [fieldValues, setFieldValues] = useState({
    name: lead?.name || "",
    email: lead?.email || "",
    phone: lead?.phone || "",
    company: lead?.company || "",
  });
  const [newNote, setNewNote] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(
    lead?.leadStatus || "New"
  );

  const [updateVisitorInfo] = useMutation(UPDATE_VISITOR_INFO, {
    onCompleted: () => {
      setEditingField(null);
      if (refetchLeads) refetchLeads();
    },
    onError: (error) => {
      console.error("Error updating lead:", error);
      alert("Failed to update lead information");
    },
  });

  const [updateLeadStatus] = useMutation(UPDATE_LEAD_STATUS, {
    onCompleted: () => {
      if (refetchLeads) refetchLeads();
    },
    onError: (error) => {
      console.error("Error updating status:", error);
      alert("Failed to update status");
    },
  });

  const [addLeadNote] = useMutation(ADD_LEAD_NOTE, {
    onCompleted: () => {
      setNewNote("");
      if (refetchLeads) refetchLeads();
    },
    onError: (error) => {
      console.error("Error adding note:", error);
      alert("Failed to add note");
    },
  });

  useEffect(() => {
    if (lead) {
      setFieldValues({
        name: lead.name || "",
        email: lead.email || "",
        phone: lead.phone || "",
        company: lead.company || "",
      });
      setSelectedStatus(lead.leadStatus || "New");
    }
  }, [lead]);

  const handleFieldChange = (field, value) => {
    setFieldValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveField = async (field) => {
    if (fieldValues[field] === (lead[field] || "")) {
      setEditingField(null);
      return;
    }

    try {
      await updateVisitorInfo({
        variables: {
          sessionId: lead.sessionId,
          [field]: fieldValues[field] || null,
        },
      });
    } catch (error) {
      console.error("Error saving field:", error);
    }
  };

  const handleCancelEdit = (field) => {
    setFieldValues((prev) => ({ ...prev, [field]: lead[field] || "" }));
    setEditingField(null);
  };

  const handleStatusChange = async (newStatus) => {
    setSelectedStatus(newStatus);
    try {
      await updateLeadStatus({
        variables: {
          sessionId: lead.sessionId,
          leadStatus: newStatus,
        },
      });
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    try {
      await addLeadNote({
        variables: {
          sessionId: lead.sessionId,
          note: newNote,
          addedBy: "Agent",
        },
      });
    } catch (error) {
      console.error("Error adding note:", error);
    }
  };

  if (!lead) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: "primary.main" }}>
              <PersonIcon />
            </Avatar>
            <Box>
              <Typography variant="h6">{lead.name || "Lead"}</Typography>
              <Typography variant="caption" color="textSecondary">
                Lead ID: {lead.sessionId}
              </Typography>
            </Box>
          </Box>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={selectedStatus}
              onChange={(e) => handleStatusChange(e.target.value)}
            >
              <MenuItem value="New">New</MenuItem>
              <MenuItem value="Contacted">Contacted</MenuItem>
              <MenuItem value="Qualified">Qualified</MenuItem>
              <MenuItem value="Converted">Converted</MenuItem>
              <MenuItem value="Lost">Lost</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
        >
          <Tab label="Contact Info" />
          <Tab label="Activity Timeline" />
          <Tab label="Notes" />
        </Tabs>

        {/* Contact Info Tab */}
        {activeTab === 0 && (
          <Box mt={3}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <PersonIcon sx={{ mr: 2 }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  Personal Information
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {/* Name Field */}
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      gutterBottom
                    >
                      Name
                    </Typography>
                    {editingField === "name" ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <TextField
                          size="small"
                          fullWidth
                          value={fieldValues.name}
                          onChange={(e) =>
                            handleFieldChange("name", e.target.value)
                          }
                          onKeyPress={(e) =>
                            e.key === "Enter" && handleSaveField("name")
                          }
                          autoFocus
                        />
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleSaveField("name")}
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleCancelEdit("name")}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography
                          variant="body1"
                          onClick={() => setEditingField("name")}
                          sx={{
                            cursor: "pointer",
                            "&:hover": { bgcolor: "action.hover" },
                            p: 0.5,
                            borderRadius: 1,
                            flex: 1,
                          }}
                        >
                          {lead.name || (
                            <span
                              style={{ color: "#999", fontStyle: "italic" }}
                            >
                              Click to add...
                            </span>
                          )}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => setEditingField("name")}
                          sx={{ p: 0.5 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Grid>

                  {/* Email Field */}
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      gutterBottom
                    >
                      Email
                    </Typography>
                    {editingField === "email" ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <TextField
                          size="small"
                          fullWidth
                          type="email"
                          value={fieldValues.email}
                          onChange={(e) =>
                            handleFieldChange("email", e.target.value)
                          }
                          onKeyPress={(e) =>
                            e.key === "Enter" && handleSaveField("email")
                          }
                          autoFocus
                        />
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleSaveField("email")}
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleCancelEdit("email")}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography
                          variant="body1"
                          onClick={() => setEditingField("email")}
                          sx={{
                            cursor: "pointer",
                            "&:hover": { bgcolor: "action.hover" },
                            p: 0.5,
                            borderRadius: 1,
                            flex: 1,
                          }}
                        >
                          {lead.email || (
                            <span
                              style={{ color: "#999", fontStyle: "italic" }}
                            >
                              Click to add...
                            </span>
                          )}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => setEditingField("email")}
                          sx={{ p: 0.5 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Grid>

                  {/* Phone Field */}
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      gutterBottom
                    >
                      Phone
                    </Typography>
                    {editingField === "phone" ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <TextField
                          size="small"
                          fullWidth
                          type="tel"
                          value={fieldValues.phone}
                          onChange={(e) =>
                            handleFieldChange("phone", e.target.value)
                          }
                          onKeyPress={(e) =>
                            e.key === "Enter" && handleSaveField("phone")
                          }
                          autoFocus
                        />
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleSaveField("phone")}
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleCancelEdit("phone")}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography
                          variant="body1"
                          onClick={() => setEditingField("phone")}
                          sx={{
                            cursor: "pointer",
                            "&:hover": { bgcolor: "action.hover" },
                            p: 0.5,
                            borderRadius: 1,
                            flex: 1,
                          }}
                        >
                          {lead.phone || (
                            <span
                              style={{ color: "#999", fontStyle: "italic" }}
                            >
                              Click to add...
                            </span>
                          )}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => setEditingField("phone")}
                          sx={{ p: 0.5 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Grid>

                  {/* Company Field */}
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      gutterBottom
                    >
                      Company
                    </Typography>
                    {editingField === "company" ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <TextField
                          size="small"
                          fullWidth
                          value={fieldValues.company}
                          onChange={(e) =>
                            handleFieldChange("company", e.target.value)
                          }
                          onKeyPress={(e) =>
                            e.key === "Enter" && handleSaveField("company")
                          }
                          autoFocus
                        />
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleSaveField("company")}
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleCancelEdit("company")}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography
                          variant="body1"
                          onClick={() => setEditingField("company")}
                          sx={{
                            cursor: "pointer",
                            "&:hover": { bgcolor: "action.hover" },
                            p: 0.5,
                            borderRadius: 1,
                            flex: 1,
                          }}
                        >
                          {lead.company || (
                            <span
                              style={{ color: "#999", fontStyle: "italic" }}
                            >
                              Click to add...
                            </span>
                          )}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => setEditingField("company")}
                          sx={{ p: 0.5 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <LanguageIcon sx={{ mr: 2 }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  Location & Device
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      Location
                    </Typography>
                    <Typography variant="body1">
                      {lead.city}, {lead.country}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      Browser
                    </Typography>
                    <Typography variant="body1">{lead.browser}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      Device
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ textTransform: "capitalize" }}
                    >
                      {lead.device}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      Total Page Views
                    </Typography>
                    <Typography variant="body1">
                      {lead.totalPageViews}
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Box>
        )}

        {/* Activity Timeline Tab */}
        {activeTab === 1 && (
          <Box mt={3}>
            <Typography variant="h6" gutterBottom>
              Activity Timeline
            </Typography>
            <Box maxHeight={400} overflow="auto">
              {lead.pageViews && lead.pageViews.length > 0 ? (
                lead.pageViews.map((pv, index) => (
                  <Box
                    key={index}
                    mb={2}
                    p={2}
                    bgcolor="grey.50"
                    borderRadius={1}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <ScheduleIcon fontSize="small" color="action" />
                      <Typography variant="body2" fontWeight="bold">
                        {pv.viewedAt
                          ? new Date(
                              typeof pv.viewedAt === "string"
                                ? Number(pv.viewedAt)
                                : pv.viewedAt
                            ).toLocaleString()
                          : "N/A"}
                      </Typography>
                    </Box>
                    <Typography variant="body2">
                      {pv.title || "Untitled"}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {pv.url}
                    </Typography>
                    {pv.duration && (
                      <Box mt={0.5}>
                        <Chip
                          label={`Duration: ${Math.floor(pv.duration / 60)}m ${
                            pv.duration % 60
                          }s`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    )}
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No activity recorded
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Notes Tab */}
        {activeTab === 2 && (
          <Box mt={3}>
            <Typography variant="h6" gutterBottom>
              Lead Notes
            </Typography>

            {/* Add Note Section */}
            <Box mb={3}>
              <TextField
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                placeholder="Add a note about this lead..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
              />
              <Box mt={1} display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddNote}
                  disabled={!newNote.trim()}
                >
                  Add Note
                </Button>
              </Box>
            </Box>

            {/* Notes List */}
            <Box maxHeight={300} overflow="auto">
              {lead.leadNotes && lead.leadNotes.length > 0 ? (
                lead.leadNotes
                  .slice()
                  .reverse()
                  .map((note, index) => (
                    <Box
                      key={index}
                      mb={2}
                      p={2}
                      bgcolor="grey.50"
                      borderRadius={1}
                    >
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <NotesIcon fontSize="small" color="action" />
                        <Typography variant="caption" color="textSecondary">
                          {note.addedBy} •{" "}
                          {note.addedAt
                            ? new Date(note.addedAt).toLocaleString()
                            : "N/A"}
                        </Typography>
                      </Box>
                      <Typography variant="body2">{note.note}</Typography>
                    </Box>
                  ))
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No notes yet
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// Main LeadOverview Component
export default function LeadOverview() {
  // Get spaceId from URL query parameters
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const spaceId = searchParams.get("spaceId");

  // State
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [viewMode, setViewMode] = useState("list");
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("firstSeen");
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLead, setSelectedLead] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  // GraphQL queries
  const { data: widgetsData, loading: widgetsLoading } = useQuery(
    GET_ALL_WIDGETS,
    {
      variables: { spaceId },
      skip: !spaceId,
    }
  );

  const {
    data: leadsData,
    loading: leadsLoading,
    error: leadsError,
    refetch,
  } = useQuery(GET_LEADS, {
    variables: {
      spaceId,
      widgetId: selectedWidget,
      limit: 1000,
      offset: 0,
    },
    skip: !spaceId,
    pollInterval: 30000,
  });

  // Filter leads: must have chatSessionId, name, and email
  const leads = (leadsData?.getVisitors || []).filter(
    (visitor) => visitor.chatSessionId && visitor.name && visitor.email
  );

  // Apply search and status filters
  const filteredLeads = leads.filter((lead) => {
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        lead.name?.toLowerCase().includes(query) ||
        lead.email?.toLowerCase().includes(query) ||
        lead.company?.toLowerCase().includes(query) ||
        lead.phone?.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }

    // Apply status filter
    if (statusFilter !== "all") {
      const leadStatus = lead.leadStatus || "New";
      if (leadStatus !== statusFilter) return false;
    }

    return true;
  });

  // Get widgets
  const widgets = widgetsData?.getAllWidgets || [];

  // Handle sorting
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Handle selection
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = filteredLeads.map((l) => l.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle view details
  const handleViewDetails = (lead) => {
    setSelectedLead(lead);
    setDetailsModalOpen(true);
  };

  // Export to CSV
  const handleExportCSV = () => {
    const headers = [
      "Date",
      "Name",
      "Email",
      "Phone",
      "Company",
      "Status",
      "Source",
      "Last Activity",
    ];
    const rows = filteredLeads.map((l) => [
      l.firstSeen
        ? new Date(
            typeof l.firstSeen === "string" ? Number(l.firstSeen) : l.firstSeen
          ).toLocaleDateString()
        : "N/A",
      l.name,
      l.email,
      l.phone || "",
      l.company || "",
      l.leadStatus || "New",
      l.widgetId || "",
      l.lastActiveAt
        ? new Date(
            typeof l.lastActiveAt === "string"
              ? Number(l.lastActiveAt)
              : l.lastActiveAt
          ).toLocaleString()
        : "N/A",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Error state
  if (!spaceId) {
    return (
      <Box p={4}>
        <Alert severity="error">
          Space ID is required. Please provide a spaceId parameter in the URL.
        </Alert>
      </Box>
    );
  }

  if (leadsError) {
    return (
      <Box p={4}>
        <Alert severity="error">
          Error loading leads: {leadsError.message}
        </Alert>
      </Box>
    );
  }

  // Sorted and paginated leads
  const sortedLeads = stableSort(filteredLeads, getComparator(order, orderBy));
  const paginatedLeads = sortedLeads.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <Helmet title="Lead Overview" />

      <Box mb={4}>
        <Breadcrumbs
          aria-label="breadcrumb"
          separator={<NavigateNextIcon fontSize="small" />}
        >
          <MuiLink color="inherit" href="/widget/dashboard">
            Dashboard
          </MuiLink>
          <Typography color="textPrimary">Lead Overview</Typography>
        </Breadcrumbs>
        <Typography variant="h3" gutterBottom display="inline" mt={2}>
          Lead Overview
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          Manage and track your qualified leads
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Sidebar - Widgets List */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Widgets
            </Typography>
            {widgetsLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Box>
                {/* All Leads */}
                <Box
                  sx={{
                    p: 2,
                    mb: 1,
                    cursor: "pointer",
                    borderLeft: selectedWidget === null ? "3px solid" : "none",
                    borderColor: "primary.main",
                    bgcolor:
                      selectedWidget === null
                        ? "action.selected"
                        : "transparent",
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                  onClick={() => setSelectedWidget(null)}
                >
                  <Typography
                    variant="body2"
                    fontWeight={selectedWidget === null ? "bold" : "normal"}
                  >
                    All Leads
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {leads.length} leads
                  </Typography>
                </Box>

                {/* Individual Widgets */}
                {widgets.map((widget) => (
                  <Box
                    key={widget.id}
                    sx={{
                      p: 2,
                      mb: 1,
                      cursor: "pointer",
                      borderLeft:
                        selectedWidget === widget.id ? "3px solid" : "none",
                      borderColor: "primary.main",
                      bgcolor:
                        selectedWidget === widget.id
                          ? "action.selected"
                          : "transparent",
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                    onClick={() => setSelectedWidget(widget.id)}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={
                        selectedWidget === widget.id ? "bold" : "normal"
                      }
                    >
                      {widget.business_details?.display_name ||
                        widget.script_id}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {leads.filter((l) => l.widgetId === widget.id).length}{" "}
                      leads
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* Status Filter */}
            <Box mt={3}>
              <Typography variant="h6" gutterBottom>
                Status
              </Typography>
              <Box>
                {[
                  "all",
                  "New",
                  "Contacted",
                  "Qualified",
                  "Converted",
                  "Lost",
                ].map((status) => (
                  <Box
                    key={status}
                    sx={{
                      p: 1.5,
                      mb: 0.5,
                      cursor: "pointer",
                      borderLeft:
                        statusFilter === status ? "3px solid" : "none",
                      borderColor: "primary.main",
                      bgcolor:
                        statusFilter === status
                          ? "action.selected"
                          : "transparent",
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                    onClick={() => setStatusFilter(status)}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography
                        variant="body2"
                        fontWeight={statusFilter === status ? "bold" : "normal"}
                      >
                        {status === "all" ? "All Status" : status}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {status === "all"
                          ? leads.length
                          : leads.filter(
                              (l) => (l.leadStatus || "New") === status
                            ).length}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Main Content - Leads Table */}
        <Grid item xs={12} md={9}>
          <Paper>
            {/* Toolbar */}
            <Toolbar
              sx={{
                pl: { sm: 2 },
                pr: { xs: 1, sm: 1 },
                ...(selected.length > 0 && {
                  bgcolor: (theme) =>
                    alpha(
                      theme.palette.primary.main,
                      theme.palette.action.activatedOpacity
                    ),
                }),
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                height: "70px",
                borderBottom: "1px solid rgba(224, 224, 224, 0.4)",
              }}
            >
              <Box>
                {selected.length > 0 ? (
                  <Typography
                    color="inherit"
                    variant="subtitle1"
                    sx={{ fontWeight: 500 }}
                  >
                    {selected.length} selected
                  </Typography>
                ) : (
                  <Box display="flex" alignItems="center" gap={2}>
                    <Search>
                      <SearchIconWrapper>
                        <SearchIcon />
                      </SearchIconWrapper>
                      <StyledInputBase
                        placeholder="Search leads..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </Search>
                    <Typography variant="body2" color="textSecondary">
                      {filteredLeads.length} leads
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box>
                {selected.length > 0 ? (
                  <Box display="flex" gap={1}>
                    {selected.length === 1 && (
                      <Tooltip title="View Details">
                        <IconButton
                          aria-label="View"
                          size="large"
                          onClick={() => {
                            const lead = filteredLeads.find(
                              (l) => l.id === selected[0]
                            );
                            if (lead) handleViewDetails(lead);
                          }}
                          sx={{ mr: 1 }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                ) : (
                  <Box display="flex" gap={1}>
                    <Tooltip title="Refresh">
                      <IconButton onClick={() => refetch()}>
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Export CSV">
                      <IconButton onClick={handleExportCSV}>
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={
                        viewMode === "list"
                          ? "Change to Card View"
                          : "Change to List View"
                      }
                    >
                      <IconButton
                        color="primary"
                        onClick={() =>
                          setViewMode(viewMode === "list" ? "grid" : "list")
                        }
                        size="medium"
                        sx={{
                          borderRadius: "8px",
                          p: 1,
                          backgroundColor: (theme) =>
                            alpha(theme.palette.primary.main, 0.08),
                          "&:hover": {
                            backgroundColor: (theme) =>
                              alpha(theme.palette.primary.main, 0.12),
                          },
                        }}
                      >
                        {viewMode === "list" ? (
                          <ViewModuleIcon />
                        ) : (
                          <ViewListIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            </Toolbar>

            {/* Table/Grid Content */}
            {leadsLoading ? (
              <Box display="flex" justifyContent="center" p={8}>
                <CircularProgress />
              </Box>
            ) : filteredLeads.length === 0 ? (
              <Box p={8} textAlign="center">
                <Typography variant="h6" color="textSecondary">
                  No leads found
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Leads will appear here once visitors provide their contact
                  information
                </Typography>
              </Box>
            ) : viewMode === "grid" ? (
              <Box p={3}>
                <Grid container spacing={3}>
                  {paginatedLeads.map((lead) => {
                    const isItemSelected = isSelected(lead.id);
                    return (
                      <Grid item xs={12} sm={6} md={4} key={lead.id}>
                        <LeadCard
                          lead={lead}
                          isSelected={isItemSelected}
                          onSelect={handleClick}
                          onViewDetails={handleViewDetails}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredLeads.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <EnhancedTableHead
                      numSelected={selected.length}
                      order={order}
                      orderBy={orderBy}
                      onSelectAllClick={handleSelectAllClick}
                      onRequestSort={handleRequestSort}
                      rowCount={filteredLeads.length}
                    />
                    <TableBody>
                      {paginatedLeads.map((lead, index) => {
                        const isItemSelected = isSelected(lead.id);
                        const labelId = `enhanced-table-checkbox-${index}`;

                        return (
                          <TableRow
                            hover
                            role="checkbox"
                            aria-checked={isItemSelected}
                            tabIndex={-1}
                            key={lead.id}
                            selected={isItemSelected}
                            onClick={() => handleViewDetails(lead)}
                            sx={{ cursor: "pointer" }}
                          >
                            <TableCell
                              padding="checkbox"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Checkbox
                                color="primary"
                                checked={isItemSelected}
                                inputProps={{ "aria-labelledby": labelId }}
                                onClick={(event) => handleClick(event, lead.id)}
                              />
                            </TableCell>
                            <TableCell>
                              {lead.firstSeen
                                ? new Date(
                                    typeof lead.firstSeen === "string"
                                      ? Number(lead.firstSeen)
                                      : lead.firstSeen
                                  ).toLocaleDateString()
                                : "N/A"}
                            </TableCell>
                            <TableCell>{lead.name}</TableCell>
                            <TableCell>{lead.email}</TableCell>
                            <TableCell>{lead.phone || "-"}</TableCell>
                            <TableCell>{lead.company || "-"}</TableCell>
                            <TableCell align="center">
                              <Chip
                                label={lead.leadStatus || "New"}
                                color={getStatusColor(lead.leadStatus || "New")}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{lead.widgetId || "-"}</TableCell>
                            <TableCell>
                              {lead.lastActiveAt
                                ? new Date(
                                    typeof lead.lastActiveAt === "string"
                                      ? Number(lead.lastActiveAt)
                                      : lead.lastActiveAt
                                  ).toLocaleString()
                                : "N/A"}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredLeads.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Lead Details Modal */}
      <LeadDetailsModal
        lead={selectedLead}
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        refetchLeads={refetch}
      />
    </>
  );
}
